import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import LHOverlay from '../../../components/LHOverlay';
import DLOTooltip from '../../../modules/dlo/components/DLOTooltip';
import FontFamily from '../../../constants/fonts';
import LhDatePicker from '../../../components/LhDatePicker';
import { InfoIcon, RadioButton } from './StandupIcons';
import { StandupItem, StandupOverlayProps } from '../types';
import { useTheme } from '../../../theme';

// Constants
const STANDUP_TYPES = [
  { 
    id: 'weekly-lt', 
    label: 'Weekly LT',
    tooltip: '• 45 min-1 hour once a week\n• Detailed review of previous week performance\n• Review of progress on action items\n• Plan for following week\'s key activities (e.g., staffing needs)'
  },
  { 
    id: 'daily-lt', 
    label: 'Daily LT',
    tooltip: '• 15 mins daily\n• Review previous day performance metrics\n• Discuss daily ops updates\n• Track action items to address operational issues'
  },
  { 
    id: 'daily-frontline', 
    label: 'Daily Frontline',
    tooltip: '• 15 mins daily\n• Review previous day performance metrics\n• Recognize team member accomplishments\n• Set plan for shift\n• Identify, resolve and escalate roadblocks as needed'
  },
];

const DAILY_TYPES = ['daily-lt', 'daily-frontline'];

// Utility function to calculate blocked date range
const getBlockedDateRange = (standupListItems: StandupItem[]): { start: Date | null; end: Date | null } => {
  if (standupListItems.length === 0) {
    return { start: null, end: null };
  }

  const today = new Date(new Date().setHours(0, 0, 0, 0));

  // Filter for weekly_lt standups whose week overlaps with today or future
  const weeklyStandups = standupListItems.filter((item: StandupItem) => {
    if (item.standup_type !== 'weekly_lt') {
      return false;
    }
    
    const standupDate = new Date(item.scheduled_date);
    standupDate.setHours(0, 0, 0, 0);
    
    // Calculate the end of the standup's week (6 days after scheduled date)
    const weekEnd = new Date(standupDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    // Include if the week end is >= today (i.e., week overlaps with today or future)
    return weekEnd >= today;
  });

  if (weeklyStandups.length === 0) {
    return { start: null, end: null };
  }

  // Get the latest weekly standup (sort descending and take first)
  const latest = weeklyStandups.sort(
    (a: StandupItem, b: StandupItem) =>
      new Date(b.scheduled_date).getTime() - new Date(a.scheduled_date).getTime()
  )[0];

  console.log('latest weekly standup for blocking:', latest);
  
  // Create blocked range: from standup date to 6 days after (7-day week)
  // Parse the date string to avoid timezone issues
  const dateStr = latest.scheduled_date.split('T')[0]; // Get YYYY-MM-DD part
  const [year, month, day] = dateStr.split('-').map(Number);
  
  const standupStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  const blockEnd = new Date(year, month - 1, day + 6, 23, 59, 59, 999);
  
  // Block from today (or standup start date, whichever is earlier) till blockEnd
  const blockStart = standupStartDate < today ? standupStartDate : today;
  return { start: blockStart, end: blockEnd };
};

const StandupOverlay: React.FC<StandupOverlayProps> = ({
  visible,
  onDismiss,
  onContinue,
  standupListItems = [],
  userRoleId = null,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // For date pickers on web, we need to check dark theme directly since web uses global CSS patcher
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // Button is enabled by default; validation happens in handleContinue
  const isContinueEnabled = true;

  // Light theme overrides for modal
  const modalBackgroundColor = isDark ? '#000000' : '#FFFFFF';
  const textColor = isDark ? '#FFFFFF' : '#000000';

  // Filter standup types based on user role
  // Roles 1, 2, 3, 4, 15 can see all types; others see only Daily Frontline
  const allowedRoles = [1, 2, 3, 4, 15];
  const canSeeAllTypes = userRoleId !== null && allowedRoles.includes(userRoleId);
  const filteredStandupTypes = useMemo(
    () => canSeeAllTypes ? STANDUP_TYPES : STANDUP_TYPES.filter(type => type.id === 'daily-frontline'),
    [canSeeAllTypes]
  );

  // Memoize blocked date range calculation
  const blockedDateRange = useMemo(
    () => getBlockedDateRange(standupListItems),
    [standupListItems]
  );

  useEffect(() => {
    if (visible) {
      setSelectedType('');
      setSelectedDate(null);
    }
  }, [visible]);

  const formatDate = (date: Date | null): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    // Return ISO format (YYYY-MM-DD) to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleStandupTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
    if (DAILY_TYPES.includes(typeId)) {
      setSelectedDate(new Date());
    } else if (typeId === 'weekly-lt') {
      setSelectedDate(null);
    }
  };

  const handleContinue = () => {
    // Validate that both type and date are selected before continuing
    if (!selectedType || !selectedDate) {
      alert('Please select both a stand-up type and a date');
      return;
    }
    onContinue(selectedType, formatDate(selectedDate));
  };

  const handleDateChange = (date: Date | null) => {
    // For Weekly LT meeting, validate that the selected date is not within blocked range
    if (selectedType === 'weekly-lt' && date && blockedDateRange.start && blockedDateRange.end) {
      const selectedDateTime = new Date(date).setHours(0, 0, 0, 0);
      const blockStart = new Date(blockedDateRange.start).setHours(0, 0, 0, 0);
      const blockEnd = new Date(blockedDateRange.end).setHours(23, 59, 59, 999);
      
      // Check if selected date falls within blocked range
      if (selectedDateTime >= blockStart && selectedDateTime <= blockEnd) {
        // Date is blocked, don't set it
        console.log('Selected date is blocked:', date);
        return;
      }
    }
    
    setSelectedDate(date);
  };

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title="Stand-up"
      titleStyle={{ fontSize: 24, lineHeight: 32, color: textColor, fontWeight: '700', fontFamily: FontFamily.bold }}
      size="xs"
      hideFooter={true}
      headerPaddingHorizontal={32}
      headerPaddingTop={32}
     
      bodyPadding={{ horizontal: 32, vertical: { top: 12, bottom: 32 } }}
      backgroundColor={modalBackgroundColor}
      hideHeaderDivider={true}

    >
      <View style={styles.container}>
        {/* Subheader */}
        <Text style={[styles.subheader, { color: textColor }]}>Create a Stand-up</Text>

        {/* Radio button options */}
        <View style={styles.radioGroup}>
          {filteredStandupTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={styles.radioItem}
              onPress={() => handleStandupTypeSelect(type.id)}
              activeOpacity={0.7}
            >
              <RadioButton selected={selectedType === type.id} color={textColor} />
              <View style={styles.textWithIcon}>
                <Text style={[styles.radioLabel, { color: textColor }]}>{type.label}</Text>
                {/* <DLOTooltip
                  content={type.tooltip}
                  position="right"
                  trigger="hover"
                >
                  <InfoIcon />
                </DLOTooltip> */}
              </View>
            </TouchableOpacity>
          ))}
        </View>

        {/* Date picker */}
        <View style={styles.datePickerWrapper}>
          <LhDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            placeholder="Select a date"
            minimumDate={new Date(new Date().setHours(0, 0, 0, 0))}
            blockedDateStart={selectedType === 'weekly-lt' && blockedDateRange.start ? blockedDateRange.start : undefined}
            blockedDateEnd={selectedType === 'weekly-lt' && blockedDateRange.end ? blockedDateRange.end : undefined}
            colorScheme={datePickerColorScheme}
            iconColor={isDark ? '#FFFFFF' : '#3A3A3A'}
            style={{
              calendarStyle: {
                boxShadow: "none",
              },
            }}
          />
        </View>

        {/* Action buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.button, styles.cancelButton]}
            onPress={onDismiss}
            activeOpacity={0.7}
          >
            <Text style={[styles.cancelButtonText, { color: textColor }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.button,
              styles.continueButton
            ]}
            onPress={handleContinue}
            activeOpacity={0.7}
          >
            <Text style={[styles.continueButtonText, { color: '#FFFFFF' }]}>
              Continue
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',  
  },
  subheader: {
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 24,
    color: '#FFFFFF',
    marginBottom: 12,
  },
  radioGroup: {
    gap: 12,
    marginBottom: 12,
  },
  radioItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  textWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  radioLabel: {
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 24,
    color: '#FFFFFF',
  },
  datePickerWrapper: {
    marginBottom: 16,
    width: '100%',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 16,
    marginBottom: 0,
  },
  button: {
    // Follow Figma-like layout: fixed height, centered content, large corner radius
    height: 40,
    paddingHorizontal: 24,
    // keep some vertical padding for touch affordance on native (mobile)
    paddingVertical: Platform.OS === 'web' ? 0 : 8,
    borderRadius: (Platform.OS === 'web' ? 'var(--Corner-radius-corner-radius-large, 24px)' : 24) as any,
    minWidth: 88,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#CCCCCC',
  },
  continueButton: {
    backgroundColor: '#0B5FC6',
  },
  cancelButtonText: {
    fontFamily: FontFamily.medium,
    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#000000',
  },
  continueButtonText: {
    fontFamily: FontFamily.bold,
    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
});

export default StandupOverlay;
